import { API_BASE_URL, apiRequest, type ApiRequestOptions } from '../http.ts';
import type { APIResponse } from './types/common.ts';
import type {
  AutomatedDataTask,
  AutomatedDataTaskRecord,
  AutomatedDataTasksData,
  AutomatedDataTaskRunResponse,
  CacheStatus,
  CreateAutomatedDataTaskParams,
  DataDownloadTask,
  DataDownloadTaskIdData,
  DataDownloadTasksData,
  DurableJobSnapshot,
  DataInventoryData,
  DataLakeSyncResponse,
  DataSeriesDeleteResponse,
  LakeSeriesStatusResponse,
  LatestTradingDayResponse,
  MarketDataInventoryItem,
  MarketDataSeries,
} from './types/data.ts';

type DataDownloadTaskParams = {
  symbol?: string;
  symbols?: string[];
  strategy?: string;
  start_date: string;
  end_date: string;
  timeframe: string;
  data_source: 'IBKR';
  sec_type?: string;
  stk_type?: string;
  exchange?: string;
  currency?: string;
  con_id?: number;
  last_trade_date_or_contract_month?: string;
  local_symbol?: string;
  trading_class?: string;
  multiplier?: string;
  storage_target?: 'both' | 'trader' | 'lake';
  data_type?: string;
  refetch?: boolean;
  fetch_profile?: string;
  chunk_days?: number;
  pacing_seconds?: number;
  max_retries?: number;
};

type MarketDataSeriesFilters = {
  symbol: string;
  timeframe: string;
  sec_type?: string;
  data_source?: string;
  storage?: string;
  data_type?: string;
  multiplier?: number;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  min_price?: number;
  max_price?: number;
  min_volume?: number;
};

const normalizeAutomatedTaskSymbols = (symbols: string | string[] | null | undefined): string[] => {
  if (Array.isArray(symbols)) return symbols;
  return (symbols || '')
    .split(/[\s,]+/)
    .map((symbol) => symbol.trim().toUpperCase())
    .filter(Boolean);
};

const normalizeAutomatedTaskDays = (scheduleDays: string | string[] | null | undefined): string[] => {
  if (Array.isArray(scheduleDays)) return scheduleDays;
  const normalized = (scheduleDays || '').trim().toUpperCase();
  if (normalized === 'MON-FRI') return ['MON', 'TUE', 'WED', 'THU', 'FRI'];
  if (normalized === 'ALL') return ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  return normalized.split(/[\s,]+/).filter(Boolean);
};

const automatedTaskPayload = (
  task: CreateAutomatedDataTaskParams | Partial<CreateAutomatedDataTaskParams>,
) => {
  const payload = { ...task } as Record<string, unknown>;
  if ('symbols' in task && task.symbols) {
    payload.symbols = task.symbols.join(', ');
  }
  if ('schedule_days' in task && task.schedule_days) {
    const days = task.schedule_days;
    const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    payload.schedule_days = days.length === 5 && weekdays.every((day) => days.includes(day))
      ? 'mon-fri'
      : days.length === 7 && days.every((day) => ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].includes(day))
        ? 'all'
        : days.join(',');
  }
  return payload;
};

const createDataDownloadIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `download-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const normalizeAutomatedTask = (task: unknown): AutomatedDataTask => {
  const normalizedTask = task && typeof task === 'object'
    ? task as Record<string, unknown>
    : {};
  return {
    ...normalizedTask,
    symbols: normalizeAutomatedTaskSymbols(normalizedTask.symbols as string | string[] | null | undefined),
    schedule_days: normalizeAutomatedTaskDays(
      normalizedTask.schedule_days as string | string[] | null | undefined,
    ),
  } as AutomatedDataTask;
};

const automatedTaskData = (
  data: AutomatedDataTaskRecord | { task: AutomatedDataTaskRecord } | null | undefined,
): AutomatedDataTaskRecord | null | undefined => (
  data && 'task' in data ? data.task : data
);

/** Market-data, download-task, and automated-fetch API endpoints. */
export class DataApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  getLatestTradingDay(): Promise<LatestTradingDayResponse> {
    return this.request<LatestTradingDayResponse>(`${this.baseUrl}/backtest/data/latest-trading-day`);
  }

  async checkBacktestCache(config: {
    strategy: string;
    symbol: string;
    start_date: string;
    end_date: string;
    timeframe?: string;
    capital?: number;
    data_source?: string;
    parameters?: Record<string, any>;
    execution_price_type?: string;
    entry_type?: string;
    exit_type?: string;
    sec_type?: string;
    refetch?: boolean;
  }): Promise<CacheStatus> {
    return this.request<CacheStatus>(`${this.baseUrl}/backtest/cache/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
  }

  async getMarketDataInventory(
    filters: {
      symbol?: string;
      timeframe?: string;
      sec_type?: string;
      data_source?: string;
      storage?: string;
      data_type?: string;
    } = {},
    pagination: { skip?: number; limit?: number } = {},
  ): Promise<{ items: MarketDataInventoryItem[]; total: number }> {
    const params = new URLSearchParams();
    if (filters.symbol) params.set('symbol', filters.symbol);
    if (filters.timeframe) params.set('timeframe', filters.timeframe);
    if (filters.sec_type) params.set('sec_type', filters.sec_type);
    if (filters.data_source) params.set('data_source', filters.data_source);
    if (filters.storage) params.set('storage', filters.storage);
    if (filters.data_type) params.set('data_type', filters.data_type);
    if (pagination.skip !== undefined) params.set('skip', String(pagination.skip));
    if (pagination.limit !== undefined) params.set('limit', String(pagination.limit));
    const query = params.toString();
    const endpoint = `${this.baseUrl}/backtest/data/inventory${query ? `?${query}` : ''}`;
    const response = await this.request<APIResponse<DataInventoryData>>(endpoint);
    if (!response.data) throw new Error('Market-data inventory response did not include data');
    return response.data;
  }

  deleteMarketDataSeries(params: {
    symbol: string;
    timeframe: string;
    sec_type?: string;
    data_source?: string;
    multiplier?: number;
    data_type?: string;
  }): Promise<DataSeriesDeleteResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('symbol', params.symbol);
    searchParams.set('timeframe', params.timeframe);
    if (params.sec_type) searchParams.set('sec_type', params.sec_type);
    if (params.data_source) searchParams.set('data_source', params.data_source);
    if (params.multiplier) searchParams.set('multiplier', String(params.multiplier));
    if (params.data_type) searchParams.set('data_type', params.data_type);
    const endpoint = `${this.baseUrl}/backtest/data/series?${searchParams.toString()}`;
    return this.request<DataSeriesDeleteResponse>(endpoint, { method: 'DELETE' });
  }

  async getLakeBackupStatus(params: {
    symbol: string;
    timeframe: string;
    sec_type?: string;
    data_source?: string;
    multiplier?: number;
    data_type?: string;
  }): Promise<LakeSeriesStatusResponse> {
    const searchParams = this.marketDataIdentityParams(params);
    const response = await this.request<LakeSeriesStatusResponse>(
      `${this.baseUrl}/backtest/data/lake-status?${searchParams.toString()}`,
    );
    return response;
  }

  async syncMarketDataToLake(params: {
    symbol: string;
    timeframe: string;
    sec_type?: string;
    data_source?: string;
    multiplier?: number;
    data_type?: string;
  }): Promise<DataLakeSyncResponse> {
    const searchParams = this.marketDataIdentityParams(params);
    const response = await this.request<DataLakeSyncResponse>(
      `${this.baseUrl}/backtest/data/sync?${searchParams.toString()}`,
      { method: 'POST' },
    );
    return response;
  }

  async getMarketDataSeries(filters: MarketDataSeriesFilters): Promise<MarketDataSeries> {
    const params = new URLSearchParams();
    params.set('symbol', filters.symbol);
    params.set('timeframe', filters.timeframe);
    if (filters.sec_type) params.set('sec_type', filters.sec_type);
    if (filters.data_source) params.set('data_source', filters.data_source);
    if (filters.storage) params.set('storage', filters.storage);
    if (filters.data_type) params.set('data_type', filters.data_type);
    if (filters.multiplier) params.set('multiplier', String(filters.multiplier));
    if (filters.start_date) params.set('start_date', filters.start_date);
    if (filters.end_date) params.set('end_date', filters.end_date);
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset !== undefined) params.set('offset', String(filters.offset));
    if (filters.sort_by) params.set('sort_by', filters.sort_by);
    if (filters.sort_dir) params.set('sort_dir', filters.sort_dir);
    if (filters.min_price !== undefined) params.set('min_price', String(filters.min_price));
    if (filters.max_price !== undefined) params.set('max_price', String(filters.max_price));
    if (filters.min_volume !== undefined) params.set('min_volume', String(filters.min_volume));
    const response = await this.request<APIResponse<MarketDataSeries>>(
      `${this.baseUrl}/backtest/data/series?${params.toString()}`,
    );
    if (!response.data) throw new Error('Market-data series response did not include data');
    return response.data;
  }

  async getDataDownloadTasks(): Promise<DataDownloadTask[]> {
    const response = await this.request<APIResponse<DataDownloadTasksData>>(
      `${this.baseUrl}/backtest/data/tasks`,
    );
    return response.data?.tasks || [];
  }

  async getDataDownloadTask(taskId: string): Promise<DataDownloadTask> {
    const response = await this.request<APIResponse<DataDownloadTask>>(
      `${this.baseUrl}/backtest/data/tasks/${encodeURIComponent(taskId)}`,
    );
    if (!response.data) throw new Error('Download task response did not include task data');
    return response.data;
  }

  async createDataDownloadTask(
    config: DataDownloadTaskParams,
    idempotencyKey?: string,
  ): Promise<DataDownloadTask> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    headers.set('Idempotency-Key', idempotencyKey || createDataDownloadIdempotencyKey());
    const response = await this.request<APIResponse<DataDownloadTask>>(
      `${this.baseUrl}/backtest/data/download`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(config),
      },
    );
    if (!response.data) throw new Error('Download task response did not include task data');
    return response.data;
  }

  cancelDataDownloadTask(taskId: string): Promise<APIResponse<DataDownloadTaskIdData>> {
    return this.request<APIResponse<DataDownloadTaskIdData>>(
      `${this.baseUrl}/backtest/data/tasks/${taskId}/cancel`,
      {
        method: 'POST',
      },
    );
  }

  async getAutomatedDataTasks(): Promise<AutomatedDataTask[]> {
    const response = await this.request<APIResponse<AutomatedDataTasksData>>(
      `${this.baseUrl}/backtest/data/automated-tasks`,
    );
    return (response.data?.tasks || []).map(normalizeAutomatedTask);
  }

  async createAutomatedDataTask(task: CreateAutomatedDataTaskParams): Promise<AutomatedDataTask> {
    const response = await this.request<APIResponse<AutomatedDataTaskRecord | { task: AutomatedDataTaskRecord }>>(`${this.baseUrl}/backtest/data/automated-tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(automatedTaskPayload(task)),
    });
    const taskRecord = automatedTaskData(response.data);
    if (!taskRecord) throw new Error('Automated-task response did not include task data');
    return normalizeAutomatedTask(taskRecord);
  }

  async updateAutomatedDataTask(
    taskId: number,
    updates: Partial<CreateAutomatedDataTaskParams>,
  ): Promise<AutomatedDataTask> {
    const response = await this.request<APIResponse<AutomatedDataTaskRecord | { task: AutomatedDataTaskRecord }>>(
      `${this.baseUrl}/backtest/data/automated-tasks/${taskId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(automatedTaskPayload(updates)),
      },
    );
    const taskRecord = automatedTaskData(response.data);
    if (!taskRecord) throw new Error('Automated-task response did not include task data');
    return normalizeAutomatedTask(taskRecord);
  }

  deleteAutomatedDataTask(taskId: number): Promise<{ success: boolean; message?: string }> {
    return this.request<{ success: boolean; message?: string }>(
      `${this.baseUrl}/backtest/data/automated-tasks/${taskId}`,
      { method: 'DELETE' },
    );
  }

  runAutomatedDataTask(
    taskId: number,
    idempotencyKey?: string,
  ): Promise<AutomatedDataTaskRunResponse> {
    const headers = new Headers();
    headers.set('Idempotency-Key', idempotencyKey || createDataDownloadIdempotencyKey());
    return this.request<AutomatedDataTaskRunResponse>(
      `${this.baseUrl}/backtest/data/automated-tasks/${taskId}/run`,
      { method: 'POST', headers },
    );
  }

  async getDurableJob(jobId: string): Promise<DurableJobSnapshot> {
    const response = await this.request<APIResponse<DurableJobSnapshot>>(
      `${this.baseUrl}/backtest/jobs/${encodeURIComponent(jobId)}`,
    );
    if (!response.data) throw new Error('Job status response did not include job data');
    return response.data;
  }

  async cancelDurableJob(jobId: string): Promise<DurableJobSnapshot> {
    const response = await this.request<APIResponse<DurableJobSnapshot>>(
      `${this.baseUrl}/backtest/jobs/${encodeURIComponent(jobId)}/cancel`,
      { method: 'POST' },
    );
    if (!response.data) throw new Error('Job cancellation response did not include job data');
    return response.data;
  }

  private marketDataIdentityParams(params: {
    symbol: string;
    timeframe: string;
    sec_type?: string;
    data_source?: string;
    multiplier?: number;
    data_type?: string;
  }): URLSearchParams {
    const searchParams = new URLSearchParams();
    searchParams.set('symbol', params.symbol);
    searchParams.set('timeframe', params.timeframe);
    if (params.sec_type) searchParams.set('sec_type', params.sec_type);
    if (params.data_source) searchParams.set('data_source', params.data_source);
    if (params.multiplier) searchParams.set('multiplier', String(params.multiplier));
    if (params.data_type) searchParams.set('data_type', params.data_type);
    return searchParams;
  }
}
